  import { useEffect, useRef, useState } from "react";
  import { Link } from "react-router-dom";
  import axios from "axios";
  import {
    ArrowRight,
    Check,
    FileText,
    Search,
    PhoneCall,
    ClipboardCheck,
    Rocket,
  } from "lucide-react";
  import { toast } from "sonner";
  import Reveal from "@/components/Reveal";
  import LottiePlayer from "@/components/LottiePlayer";
  import barsAnim from "@/assets/lottie/bars-growth.json";
  import GrowthChartBg from "@/components/GrowthChartBg";
  import { COUNTRY_CODES, CONTACT } from "@/lib/constants";
  import { trackEvent } from "@/hooks/useAnalytics";
  import FinalCTA from "@/components/FinalCTA";

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const REQUIREMENT_TYPES = [
    "Hiring - Off Campus/On Campus",
    "Sales Growth Strategy",
    "Founder Visibility",
    "PR & Media",
    "Social Media Strategy",
    "Website / App Development",
    "Workflow Automation",
    "General Discussion",
  ];

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const PROCESS_STEPS = [
  {
    n: 1,
    title: "Share Your Requirement",
    desc: "Tell us what you'd like to discuss and suggest a preferred meeting time.",
    Icon: FileText,
  },
  {
    n: 2,
    title: "Confirmation",
    desc: "We'll review your request and confirm your meeting via WhatsApp or Email.",
    Icon: Check,
  },
  {
    n: 3,
    title: "Strategy Discussion",
    desc: "Speak directly with our founder or team to understand your goals and explore solutions.",
    Icon: PhoneCall,
  },
  {
    n: 4,
    title: "Recommendations",
    desc: "We'll recommend the most suitable approach, whether it's hiring, growth, technology or visibility.",
    Icon: ClipboardCheck,
  },
  {
    n: 5,
    title: "Next Steps",
    desc: "If we're a good fit, we'll define the roadmap and begin execution.",
    Icon: Rocket,
  },
];

  const initialState = {
    name: "",
    company: "",
    email: "",
    country_code: "+91",
    phone: "",
    website: "",
    discussion_topic: "",
    message: "",
    preferred_date: "",
    preferred_time: "",
  };

  const ApplicationFlow = () => {
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
      trackEvent("consultation_start", { section: "application-form" });
    }, []);

    const update = (k) => (e) => {
      const v = e.target.value;
      setForm((s) => ({ ...s, [k]: v }));
      if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
    };

    const onPhone = (e) => {
      const numeric = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
      setForm((s) => ({ ...s, phone: numeric }));
      if (errors.phone) setErrors((s) => ({ ...s, phone: undefined }));
    };

    const validate = () => {
      const e = {};
      if (!form.name.trim()) e.name = "Please enter your name.";
      if (!form.company.trim()) e.company = "Please enter your company.";
      if (!form.email.trim()) e.email = "Email is required.";
      else if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address.";
      if (!form.phone.trim()) e.phone = "Phone number is required.";
      else if (form.phone.length < 6) e.phone = "Enter a valid phone number.";
      if (!form.discussion_topic)
      e.discussion_topic = "Please choose a discussion topic.";

      if (!form.message.trim())
      e.message = "Please tell us what you'd like to discuss.";

      if (!form.preferred_date)
      e.preferred_date = "Please select a preferred date.";

      if (!form.preferred_time)
      e.preferred_time = "Please select a preferred time.";
      return e;
    };

    const submit = async (ev) => {
      ev.preventDefault();
      const e = validate();
      setErrors(e);
      if (Object.keys(e).length) {
        toast.error("Please fix the highlighted fields.");
        const first = formRef.current?.querySelector("[data-error='true']");
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setSubmitting(true);
      try {
        const payload = {
          name: form.name,
          company: form.company,
          email: form.email,
          phone: `${form.country_code} ${form.phone}`,
          website: form.website,
          discussion_topic: form.discussion_topic,
          message: form.message,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
        };
        const { data: consultation } = await axios.post(`${API}/applications`, payload);
        trackEvent("consultation_submitted", {
          label: form.selected_plan_id || "discovery-call",
          section: "application-form",
        });

        setSubmitted(true);
        toast.success("Strategy Call Request Received.");
      } catch (err) {
        console.error(err);
        toast.error(`Something went wrong. Please email ${CONTACT.email}`);
      } finally {
        setSubmitting(false);
      }
    };

    if (submitted) {
      return (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12 shadow-[0_24px_60px_-30px_rgba(10,37,64,0.25)] text-center" data-testid="apply-success">
          <div className="w-14 h-14 rounded-full bg-[var(--hop-green)] inline-flex items-center justify-center mx-auto">
            <Check className="w-7 h-7 text-white" />
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-bold text-hop-blue mt-5 tracking-tight">
            Strategy Call Request Received
          </h3>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md mx-auto">
            Thank you.

            We've received your request.

            Our team will review it and confirm your meeting via WhatsApp or Email within one business day.

            If your preferred time isn't available, we'll suggest the closest available slot.
          </p>
        </div>
      );
    }

    const F = ({ k, label, type = "text", placeholder = "", required = true, full = false, autoComplete }) => (
      <div className={full ? "md:col-span-2" : ""} data-error={errors[k] ? "true" : undefined}>
        <label className="block text-sm font-semibold text-hop-blue mb-2">
          {label}
          {required && <span className="text-[var(--hop-green)] ml-1">*</span>}
        </label>
        <input
          type={type}
          value={form[k]}
          onChange={update(k)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`hop-input ${errors[k] ? "!border-red-500 !ring-0" : ""}`}
          data-testid={`apply-input-${k}`}
        />
        {errors[k] && <div className="mt-1.5 text-xs text-red-600">{errors[k]}</div>}
      </div>
    );

    const S = ({ k, label, options, required = true, full = false }) => (
      <div className={full ? "md:col-span-2" : ""} data-error={errors[k] ? "true" : undefined}>
        <label className="block text-sm font-semibold text-hop-blue mb-2">
          {label}
          {required && <span className="text-[var(--hop-green)] ml-1">*</span>}
        </label>
        <select
          value={form[k]}
          onChange={update(k)}
          className={`hop-input ${errors[k] ? "!border-red-500" : ""}`}
          data-testid={`apply-input-${k.replace(/_/g, "-")}`}
        >
          <option value="">Select…</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        {errors[k] && <div className="mt-1.5 text-xs text-red-600">{errors[k]}</div>}
      </div>
    );

    return (
      <form
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-[0_24px_60px_-30px_rgba(10,37,64,0.25)]"
        data-testid="application-form"
      >
        <div className="mb-6">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--hop-green)]">
            Tell us what you'd like to discuss.
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-hop-blue mt-2 tracking-tight">
            Tell us what you'd like to discuss.
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {F({ k: "name", label: "Full Name", autoComplete: "name" })}
          {F({ k: "company", label: "Company Name", autoComplete: "organization" })}
          {F({ k: "email", label: "Work Email", type: "email", autoComplete: "email" })}

          {/* Phone with country code */}
          <div className="md:col-span-2" data-error={errors.phone ? "true" : undefined}>
            <label className="block text-sm font-semibold text-hop-blue mb-2">
              Phone / WhatsApp <span className="text-[var(--hop-green)] ml-1">*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={form.country_code}
                onChange={update("country_code")}
                className="hop-input !w-auto !pr-8"
                data-testid="apply-input-country-code"
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}  {c.country}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={onPhone}
                placeholder="Phone number"
                className={`hop-input flex-1 ${errors.phone ? "!border-red-500" : ""}`}
                data-testid="apply-input-phone"
                autoComplete="tel-national"
              />
            </div>
            {errors.phone && <div className="mt-1.5 text-xs text-red-600">{errors.phone}</div>}
          </div>

          {F({
            k: "website",
            label: "Website",
            type: "url",
            required: false,
            placeholder: "https://",
            full: true,
            autoComplete: "url",
          })}
          <S
    k="discussion_topic"
    label="What would you like to discuss?"
    options={REQUIREMENT_TYPES}
    full
/>

<div
    className="md:col-span-2"
    data-error={errors.message ? "true" : undefined}
>

    <label className="block text-sm font-semibold text-hop-blue mb-2">
        Tell us briefly about your requirement
        <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <textarea
        rows={5}
        value={form.message}
        onChange={update("message")}
        className={`hop-input ${errors.message ? "!border-red-500" : ""}`}
        placeholder="Example: We'd like to hire a founding engineer, discuss business growth, explore founder visibility, AI automation, partnerships, or anything else."
    />

    {errors.message && (
        <div className="mt-1.5 text-xs text-red-600">
            {errors.message}
        </div>
    )}

</div>

<div data-error={errors.preferred_date ? "true" : undefined}>

    <label className="block text-sm font-semibold text-hop-blue mb-2">
        Preferred Meeting Date
        <span className="text-[var(--hop-green)] ml-1">*</span>
    </label>

    <input
        type="date"
        value={form.preferred_date}
        onChange={update("preferred_date")}
        className={`hop-input ${errors.preferred_date ? "!border-red-500" : ""}`}
    />

    {errors.preferred_date && (
        <div className="mt-1.5 text-xs text-red-600">
            {errors.preferred_date}
        </div>
    )}

        </div>

        <div data-error={errors.preferred_time ? "true" : undefined}>

            <label className="block text-sm font-semibold text-hop-blue mb-2">
                Preferred Meeting Time
                <span className="text-[var(--hop-green)] ml-1">*</span>
            </label>

            <input
                type="time"
                value={form.preferred_time}
                onChange={update("preferred_time")}
                className={`hop-input ${errors.preferred_time ? "!border-red-500" : ""}`}
            />

            {errors.preferred_time && (
                <div className="mt-1.5 text-xs text-red-600">
                    {errors.preferred_time}
                </div>
    )}

        </div>

        <div className="mt-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-xs text-slate-500 max-w-sm">
            Your preferred meeting time is a request.

            We'll confirm your meeting via WhatsApp or Email within one business day.

            If your selected slot isn't available, we'll coordinate the nearest available time.
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary btn-emerald justify-center"
            data-testid="application-form-submit"
            data-track="Request Free Strategy Call"
          >
            {submitting ? "Submitting…" : "Request Free Strategy Call"}
            <ArrowRight className="arrow w-4 h-4" />
          </button>

        </div>

      </div>

    </form>

  );
};

  const ProcessRail = () => (
    <div className="lg:sticky lg:top-28">
      <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--hop-green)]">
        What happens next
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-bold text-hop-blue mt-2 tracking-tight leading-tight">
        A simple, founder-led process.
      </h3>

      <ol className="mt-8 relative">
        <span className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-200" />
        <span
          className="absolute left-[19px] top-2 w-px bg-gradient-to-b from-[var(--hop-blue)] to-[var(--hop-green)]"
          style={{ height: "82%" }}
        />
        {PROCESS_STEPS.map(({ n, title, desc, Icon }) => (
          <li key={n} className="relative pl-14 pb-8 last:pb-0" data-testid={`process-step-${n}`}>
            <span className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border border-[var(--hop-green)] text-[var(--hop-green)] flex items-center justify-center shadow-[0_8px_24px_-12px_rgba(0,168,107,0.5)]">
              <Icon className="w-4.5 h-4.5" strokeWidth={2} />
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Step {n}
              </span>
            </div>
            <h4 className="font-display text-lg font-semibold text-hop-blue mt-1">
              {title}
            </h4>
            <p className="text-slate-600 text-sm mt-1 leading-relaxed">{desc}</p>
          </li>
        ))}
      </ol>

      <div className="mt-10 p-5 rounded-xl bg-[var(--hop-grey-50)] border border-slate-200">
        <div className="font-display font-semibold text-hop-blue">Have a quick question?</div>
        <a
          className="text-sm text-slate-600 hover:text-[var(--hop-green)] transition"
          href={CONTACT.emailHref}
        >
          {CONTACT.email}
        </a>
      </div>
    </div>
  );

  export default function Consultation() {
    // Anchor scroll
    useEffect(() => {
      if (window.location.hash === "#apply") {
        setTimeout(() => {
          document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    }, []);

    return (
      <div data-testid="page-consultation">
        <section className="relative bg-white py-20 md:py-28 overflow-hidden">
          <GrowthChartBg variant="light" />
          <div className="hidden xl:block absolute right-[6%] top-16 w-[300px]" aria-hidden="true"><LottiePlayer data={barsAnim} /></div>
          <div className="hop-container relative max-w-4xl">
            <Reveal>
              <div className="text-[13px] tracking-[0.22em] uppercase font-bold text-[var(--hop-green)]">
                Business Requirements
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display text-5xl md:text-7xl font-semibold text-hop-blue mt-4 tracking-[-0.025em] leading-[1.04]">
                Tell us what your business <span className="text-gradient-bg">needs.</span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-7 space-y-4 text-lg md:text-xl text-slate-700 max-w-2xl leading-relaxed font-medium">
                <p>
                  Whether you're hiring talent, implementing AI, building software, improving operations or planning growth, tell us about your requirement and we'll recommend the best solution.
                </p>
                <p className="text-slate-600">
                  Every enquiry is reviewed personally. If there's a fit, our team will connect with you within one business day to discuss the next steps.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Pricing section removed */}

        {/* Two-column: Form left, Process right */}
        <section id="apply" className="py-20 md:py-32 section-surface" data-testid="apply-section">
          <div className="hop-container">
            <Reveal>
              <div className="max-w-3xl mb-10">
                <div className="text-xs tracking-[0.22em] uppercase font-semibold text-[var(--hop-green)]">
                  Apply
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-hop-blue mt-3 tracking-tight leading-tight">
                  Book a Free Strategy Call.
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Tell us a little about your business and suggest a convenient meeting time.

                  We'll review your request personally and send you a confirmation via WhatsApp or Email.

                  If your preferred time isn't available, we'll coordinate the nearest available slot.
                </p>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <Reveal>
                  <ApplicationFlow />
                </Reveal>
              </div>
              <div className="lg:col-span-5">
                <Reveal delay={120}>
                  <ProcessRail />
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        <FinalCTA />
      </div>
    );
  }
